import { from } from 'rxjs'
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators'
import { errorNotification, ServicePrincipalService } from '~/Services'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { RootState } from '../reducers'
import { RootEpic } from '../types'
import { ServicePrincipalActions } from './slice'

// Service Principals are owned by an Organization: the API rejects an unscoped
// query/create with 403 for every caller without the System role. Resolved from
// the store (not passed in by each view) so every dispatch site stays scoped to
// whichever Organization the user is currently working in.
const currentOrganizationId = (state: RootState) =>
  getDefaultOrganization(state)?.guid as string | undefined

const getServicePrincipalsEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(ServicePrincipalActions.getServicePrincipals.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const params = {
        organizationId: currentOrganizationId(state),
        page: 1,
        pageSize: state.servicePrincipal.pageMeta?.pageSize || 10,
        ...action.payload,
      }
      return from(
        ServicePrincipalService.Get.getServicePrincipals(params),
      ).pipe(
        map(res => ServicePrincipalActions.getServicePrincipalsSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ServicePrincipalActions.getServicePrincipalsFail()]
        }),
      )
    }),
  )
}

const createServicePrincipalEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(ServicePrincipalActions.createServicePrincipal.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      return from(
        ServicePrincipalService.Post.createServicePrincipal({
          organizationId: currentOrganizationId(state) as string,
          ...action.payload,
        }),
      ).pipe(
        concatMap(() => [
          ServicePrincipalActions.createServicePrincipalSuccess(),
          ServicePrincipalActions.getServicePrincipals({
            status: state.servicePrincipal.currentStatus,
            page: 1,
            pageSize: state.servicePrincipal.pageMeta?.pageSize || 10,
          }),
        ]),
        catchError(error => {
          errorNotification(error)
          return [ServicePrincipalActions.createServicePrincipalFail()]
        }),
      )
    }),
  )
}

const updateServicePrincipalEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(ServicePrincipalActions.updateServicePrincipal.match),
    switchMap(action => {
      const { id, ...data } = action.payload
      return from(
        ServicePrincipalService.Put.updateServicePrincipal(id, data),
      ).pipe(
        map(res =>
          ServicePrincipalActions.updateServicePrincipalSuccess(res.data),
        ),
        catchError(error => {
          errorNotification(error)
          return [ServicePrincipalActions.updateServicePrincipalFail()]
        }),
      )
    }),
  )
}

const deleteServicePrincipalEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(ServicePrincipalActions.deleteServicePrincipal.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const id = action.payload
      return from(
        ServicePrincipalService.Delete.deleteServicePrincipal(id),
      ).pipe(
        concatMap(() => [
          ServicePrincipalActions.deleteServicePrincipalSuccess(),
          ServicePrincipalActions.getServicePrincipals({
            status: state.servicePrincipal.currentStatus,
            page: 1,
            pageSize: state.servicePrincipal.pageMeta?.pageSize || 10,
          }),
        ]),
        catchError(error => {
          errorNotification(error)
          return [ServicePrincipalActions.deleteServicePrincipalFail()]
        }),
      )
    }),
  )
}

const getServicePrincipalDetailEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(ServicePrincipalActions.getServicePrincipalDetail.match),
    switchMap(action => {
      const id = action.payload
      return from(
        ServicePrincipalService.Get.getServicePrincipalById(id),
      ).pipe(
        map(res =>
          ServicePrincipalActions.getServicePrincipalDetailSuccess(res.data),
        ),
        catchError(error => {
          errorNotification(error)
          return [ServicePrincipalActions.getServicePrincipalDetailFail()]
        }),
      )
    }),
  )
}

const assignRoleEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(ServicePrincipalActions.assignRole.match),
    switchMap(action => {
      const { servicePrincipalId, roleId } = action.payload
      return from(
        ServicePrincipalService.Post.assignRole(servicePrincipalId, roleId),
      ).pipe(
        map(res => ServicePrincipalActions.assignRoleSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ServicePrincipalActions.assignRoleFail()]
        }),
      )
    }),
  )
}

const removeRoleEpic: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(ServicePrincipalActions.removeRole.match),
    switchMap(action => {
      const { servicePrincipalId, roleId } = action.payload
      return from(
        ServicePrincipalService.Delete.removeRole(servicePrincipalId, roleId),
      ).pipe(
        map(() => ServicePrincipalActions.removeRoleSuccess(roleId)),
        catchError(error => {
          errorNotification(error)
          return [ServicePrincipalActions.removeRoleFail()]
        }),
      )
    }),
  )
}

const getServicePrincipalRolesEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ServicePrincipalActions.getServicePrincipalRoles.match),
    // mergeMap, not switchMap: the list page dispatches one of these per row
    // synchronously in a loop — switchMap would cancel every request but the
    // last one before its response ever arrives.
    mergeMap(action => {
      const servicePrincipalId = action.payload
      return from(
        ServicePrincipalService.Get.getServicePrincipalById(servicePrincipalId),
      ).pipe(
        map(res =>
          ServicePrincipalActions.getServicePrincipalRolesSuccess({
            servicePrincipalId,
            roles: res.data.roles,
          }),
        ),
        catchError(error => {
          errorNotification(error)
          return [
            ServicePrincipalActions.getServicePrincipalRolesFail(
              servicePrincipalId,
            ),
          ]
        }),
      )
    }),
  )
}

export const ServicePrincipalEpics = [
  getServicePrincipalsEpic,
  createServicePrincipalEpic,
  updateServicePrincipalEpic,
  deleteServicePrincipalEpic,
  getServicePrincipalDetailEpic,
  assignRoleEpic,
  removeRoleEpic,
  getServicePrincipalRolesEpic,
]
